// pages/FormViewerPage/hooks/useFormViewer/state.ts — Core data states + setters + refs
import { useState, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Form, Order, ShippingSettings, PaymentSettings, MessageTemplates, VariantDisplayStyle } from '../../../../types';
import { useWhatsApp } from '../../../../hooks/useWhatsApp';
import { SettingsContext } from '../../../../contexts/SettingsContext';
import { useToast } from '../../../../contexts/ToastContext';
import type { AddressData } from '../../../../components/AddressInput';
import type { EverproRate, BrandEverproStatus } from '../../../../services/everproAPI';
import { usePixelStates } from './statePixels';
import { useCouponStates } from './stateCoupon';
import { readBakedForm } from '../../services/formStaticAllowlist';

export function useFormViewerState(identifier: string) {
    const navigate = useNavigate();
    const { trackingSettings: globalTrackingSettings, whatsappSettings, websiteSettings, loading: settingsLoading } = useContext(SettingsContext);
    const { sendOrderConfirmation } = useWhatsApp();
    const { showToast } = useToast();

    // Hydrasi statis: bila HTML statis menyertakan __FORM_DATA__ utk slug ini,
    // render pertama langsung berisi konten (tanpa skeleton), identik dgn SSR.
    const [bakedForm] = useState<Record<string, unknown> | null>(() => readBakedForm(identifier));
    const [form, setForm] = useState<Form | null>((bakedForm as unknown as Form) || null);
    const [loading, setLoading] = useState(!bakedForm);
    const [notFound, setNotFound] = useState(false);
    const [formInactive, setFormInactive] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submission, setSubmission] = useState<{ success: boolean, order?: Order, error?: string, retrying?: boolean }>({ success: false });
    const [imageLoaded, setImageLoaded] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [variantStock, setVariantStock] = useState<Record<string, number>>({});
    const [checkoutCount, setCheckoutCount] = useState(0);
    const [productOptionsOverride, setProductOptionsOverride] = useState<Form['productOptions']>([]);
    const [linkedProductName, setLinkedProductName] = useState<string>('');
    const [messageTemplates, setMessageTemplates] = useState<MessageTemplates | null>(null);
    const pixels = usePixelStates();
    const coupon = useCouponStates();
    // company_website = honeypot anti-bot (hidden, manusia tak pernah isi).
    const [customerData, setCustomerData] = useState({ name: '', whatsapp: '', email: '', address: '', province: '', city: '', district: '', village: '', company_website: '' });
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [addressData, setAddressData] = useState<AddressData>({ province: '', city: '', district: '', village: '', detailAddress: '', fullAddress: '' });
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    const [selectedShippingKey, setSelectedShippingKey] = useState<keyof ShippingSettings | undefined>();
    const [selectedPaymentKey, setSelectedPaymentKey] = useState<keyof PaymentSettings | undefined>();
    // Metode Duitku terpilih (nilai DUITKU_FORM_METHODS) — hanya dipakai saat selectedPaymentKey === 'duitku'.
    const [selectedDuitkuMethod, setSelectedDuitkuMethod] = useState<string | null>(null);
    const [selectedEverproRate, setSelectedEverproRate] = useState<EverproRate | null>(null);
    const [selectedBiteshipRate, setSelectedBiteshipRate] = useState<any>(null);
    const [biteshipApiKey, setBiteshipApiKey] = useState<string>('');
    const [brandEverpro, setBrandEverpro] = useState<BrandEverproStatus>({ configured: false });
    // Brand dengan Mengantar saja (tanpa Everpro) tetap dapat daftar tarif —
    // fetchMergedRates menggabung pool EV+MT bila brand punya kredensial MT.
    const [brandMengantar, setBrandMengantar] = useState<{ configured: boolean }>({ configured: false });
    // Sama untuk Lincah (aturan 2026-10-20) — pool EV+MT+LC bila ada kredensial.
    const [brandLincah, setBrandLincah] = useState<{ configured: boolean }>({ configured: false });
    const [forceShowPostalCode, setForceShowPostalCode] = useState<boolean>(false);
    const [quantity, setQuantity] = useState(1);
    const [urlVariantParams, setUrlVariantParams] = useState<Record<string, string>>({});
    const [herbalClaimMode, setHerbalClaimMode] = useState<'wa' | 'claim_ongkir'>('wa');

    const debounceTimer = useRef<number | null>(null);
    const hasSubmittedRef = useRef(false);
    const submitLockRef = useRef(false);
    const curComboRef = useRef<any>(null);
    const shippingCostRef = useRef(0);
    const rateHoldRef = useRef(false); // hold popup tarif (submit guard; ref anti-render)
    // Streaming kurir (speed 2026-10-22): false = masih ada provider yg jalan.
    // Lanjut/submit dikunci sampai true (aturan user) — ref anti-render.
    const ratesSettledRef = useRef(true);
    // Rute-lemah (aturan user 2026-10-17): true bila tak ada kurir ≥80% di rute
    // ini — ongkir tetap tampil, tapi COD dikunci (state agar payment re-render).
    const [lowPerfNoCod, setLowPerfNoCod] = useState(false);
    const beforeUnloadRef = useRef({ form: null as any, customerData: null as any, addressData: null as any, selectedShippingKey: null as any, quantity: 1 as number });

    const applyOrderUpdates = useCallback((updates: Partial<Order>) => {
        setSubmission(prev => prev.order ? { ...prev, order: { ...prev.order, ...updates } } : prev);
    }, []);

    return {
        identifier, navigate,
        globalTrackingSettings, whatsappSettings, websiteSettings, settingsLoading,
        sendOrderConfirmation, showToast,
        ...pixels, ...coupon,
        bakedForm,
        form, setForm, loading, setLoading, notFound, setNotFound, formInactive, setFormInactive,
        isSubmitting, setIsSubmitting, submission, setSubmission,
        imageLoaded, setImageLoaded, timeLeft, setTimeLeft, variantStock, setVariantStock,
        checkoutCount, setCheckoutCount, productOptionsOverride, setProductOptionsOverride,
        linkedProductName, setLinkedProductName, messageTemplates, setMessageTemplates,
        customerData, setCustomerData, fieldErrors, setFieldErrors, addressData, setAddressData,
        selectedOptions, setSelectedOptions, selectedShippingKey, setSelectedShippingKey,
        selectedPaymentKey, setSelectedPaymentKey, selectedDuitkuMethod, setSelectedDuitkuMethod,
        selectedEverproRate, setSelectedEverproRate,
        selectedBiteshipRate, setSelectedBiteshipRate, biteshipApiKey, setBiteshipApiKey,
        brandEverpro, setBrandEverpro,
        brandMengantar, setBrandMengantar,
        brandLincah, setBrandLincah,
        forceShowPostalCode, setForceShowPostalCode, quantity, setQuantity,
        urlVariantParams, setUrlVariantParams,
        herbalClaimMode, setHerbalClaimMode,
        debounceTimer, hasSubmittedRef, submitLockRef, curComboRef, shippingCostRef, beforeUnloadRef, rateHoldRef,
        ratesSettledRef, lowPerfNoCod, setLowPerfNoCod,
        applyOrderUpdates,
    };
}

export type FormViewerState = ReturnType<typeof useFormViewerState>;
